import React, { useMemo, useState } from 'react';
import { put, remove, generateId } from '../hooks/useDB';
import {
  GraduationCap, Plus, X, ChevronLeft, Users, ClipboardList, NotebookPen, FileSignature,
  CalendarDays, UserCheck, CheckCircle2, Clock, Loader2, ThumbsUp, RotateCcw, Trash2,
  Upload, Send, ShieldCheck, AlertTriangle, Info, Star, Gavel, Download, Sparkles,
} from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';
import CommentThread from '../components/onboarding/CommentThread';
import {
  ONBOARDING_DAYS, JOURNEY_STATUS, TASK_STATUS, SCORECARD_DIMENSIONS, RECOMMENDATIONS,
  DOCS_RELEASABLE_STATUSES, DEFAULT_PLAN, addDays, currentDay, fmtDate, fmtDateTime,
  journeyProgress, missingLogDays, today,
} from '../data/onboardingPlan';

const MAX_DOC_MB = 5;
const MAX_DOC_BYTES = MAX_DOC_MB * 1024 * 1024;

export default function OnboardingAdmin({ currentUser = {}, isAdmin, journeys, tasks, logs, docs, attendees }) {
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const selected = journeys.find((j) => j.id === selectedId) || null;

  const forJourney = (list, id) => list.filter((r) => r.onboardingId === id);

  if (selected) {
    return (
      <JourneyDetail
        currentUser={currentUser}
        isAdmin={isAdmin}
        journey={selected}
        tasks={forJourney(tasks, selected.id)}
        logs={forJourney(logs, selected.id)}
        docs={forJourney(docs, selected.id)}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  // ── Portfolio view ────────────────────────────────────────────────────────
  const active = journeys.filter((j) => j.status === 'active' || j.status === 'review');
  const submittedCount = tasks.filter((t) => t.status === 'submitted').length;
  const decisionsDue = journeys.filter(
    (j) => (j.status === 'active' || j.status === 'review') && currentDay(j.startDate) >= ONBOARDING_DAYS
  ).length;
  const unsignedDocs = docs.filter((d) => d.published && !d.signedAt).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <GraduationCap style={{ color: 'var(--accent)' }} size={24} />
            <span>Engineer Onboarding</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? `Run every new engineer's ${ONBOARDING_DAYS}-day trial: tasks, daily logs, the hire decision and contract signing.`
              : `Engineers you're mentoring through their ${ONBOARDING_DAYS}-day plan.`}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setCreating(true)}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm"
          >
            <Plus size={16} /> <span>New Engineer</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="In progress" value={active.length} icon={Users} tone="text-sky-500" border="border-sky-500" sub="Engineers on a plan" />
        <Stat label="Awaiting review" value={submittedCount} icon={Clock} tone="text-amber-500" border="border-amber-500" sub="Tasks submitted to you" />
        <Stat label="Decisions due" value={decisionsDue} icon={Gavel} tone="text-red-500" border="border-red-500" sub={`Day ${ONBOARDING_DAYS} reached`} />
        <Stat label="Awaiting signature" value={unsignedDocs} icon={FileSignature} tone="text-emerald-500" border="border-emerald-500" sub="Documents shared" />
      </div>

      {journeys.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <GraduationCap className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No onboarding journeys yet</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {isAdmin
              ? 'Add a newly hired engineer, assign their mentor and their 15-day plan starts here.'
              : "You're not mentoring anyone right now. An admin assigns mentors when a new engineer is hired."}
          </p>
          {isAdmin && (
            <button onClick={() => setCreating(true)} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition">
              <Plus size={16} /> Add the first engineer
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {journeys.map((j) => {
            const jTasks = forJourney(tasks, j.id);
            const progress = journeyProgress(jTasks);
            const day = currentDay(j.startDate);
            const meta = JOURNEY_STATUS[j.status] || JOURNEY_STATUS.active;
            const pending = jTasks.filter((t) => t.status === 'submitted').length;
            return (
              <button
                key={j.id}
                onClick={() => setSelectedId(j.id)}
                className="text-left bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:border-slate-400 hover:shadow transition space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{j.engineerName}</div>
                    <div className="text-xs text-slate-500 truncate">{j.engineerEmail}</div>
                    <div className="text-xs text-slate-600 mt-1">{j.role || 'Engineer'}</div>
                  </div>
                  <div className="shrink-0 text-right space-y-1">
                    <span className={`sh-badge ${meta.badge}`}>{meta.label}</span>
                    {pending > 0 && (
                      <div className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                        {pending} to review
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1"><UserCheck size={12} /> {j.mentorName || 'No mentor'}</span>
                  <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {day ? `Day ${day}/${ONBOARDING_DAYS}` : `Starts ${fmtDate(j.startDate)}`}</span>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">Tasks approved</span>
                    <span className="font-semibold text-slate-700">{progress.approved}/{progress.total}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${progress.percent}%`, background: 'var(--accent)' }} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {creating && (
        <NewEngineerDrawer
          currentUser={currentUser}
          attendees={attendees}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); setSelectedId(id); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, border, sub }) {
  return (
    <div className={`sh-stat-card border-l-4 ${border}`}>
      <div className="sh-stat-head">
        <span className="sh-stat-label">{label}</span>
        <Icon className={tone} size={14} />
      </div>
      <div className="sh-stat-value text-slate-900 font-display">{value}</div>
      <span className="text-xs text-slate-500">{sub}</span>
    </div>
  );
}

// ── Journey detail ──────────────────────────────────────────────────────────
function JourneyDetail({ currentUser, isAdmin, journey, tasks, logs, docs, onBack }) {
  const [tab, setTab] = useState('tasks');
  const day = currentDay(journey.startDate);
  const progress = journeyProgress(tasks);
  const meta = JOURNEY_STATUS[journey.status] || JOURNEY_STATUS.active;
  const missing = missingLogDays(journey.startDate, logs);
  const pending = tasks.filter((t) => t.status === 'submitted').length;
  const unsigned = docs.filter((d) => d.published && !d.signedAt).length;

  const TABS = [
    { id: 'tasks', label: 'Tasks', icon: ClipboardList, count: pending },
    { id: 'logs', label: 'Work Logs', icon: NotebookPen, count: 0 },
    { id: 'review', label: 'Review & Decision', icon: Gavel, count: 0 },
    { id: 'docs', label: 'Documents', icon: FileSignature, count: unsigned },
  ];

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition">
        <ChevronLeft size={16} /> All engineers
      </button>

      {/* Engineer header */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold font-display text-slate-900">{journey.engineerName}</h2>
            <div className="text-sm text-slate-500">{journey.engineerEmail}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`sh-badge ${meta.badge}`}>{meta.label}</span>
            {day > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold text-white" style={{ background: 'var(--accent)' }}>
                <CalendarDays size={14} /> Day {day}/{ONBOARDING_DAYS}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 pt-1">
          <Fact label="Role" value={journey.role || '—'} />
          <Fact label="Mentor" value={journey.mentorName || '—'} sub={journey.mentorEmail} />
          <Fact label="Window" value={`${fmtDate(journey.startDate)} → ${fmtDate(journey.endDate)}`} />
          <Fact
            label="Tasks approved"
            value={`${progress.approved}/${progress.total}`}
            sub={progress.total ? `${progress.percent}% complete` : 'No tasks yet'}
          />
        </div>
      </div>

      {/* Mentor nudges */}
      {missing.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-sm text-amber-900">
          <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={16} />
          <div>
            <span className="font-semibold block">No work log for day {missing.join(', ')}</span>
            Worth a quick check-in — silent days are the earliest signal that someone is stuck.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Icon size={15} /> {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-white/20' : 'bg-amber-100 text-amber-700'}`}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'tasks' && <AdminTasksTab currentUser={currentUser} journey={journey} tasks={tasks} />}
      {tab === 'logs' && <AdminLogsTab currentUser={currentUser} logs={logs} />}
      {tab === 'review' && <ReviewTab currentUser={currentUser} journey={journey} tasks={tasks} logs={logs} />}
      {tab === 'docs' && <AdminDocsTab currentUser={currentUser} isAdmin={isAdmin} journey={journey} docs={docs} />}
    </div>
  );
}

function Fact({ label, value, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="font-semibold text-slate-900 text-sm mt-0.5 truncate">{value}</div>
      {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
    </div>
  );
}

// ── Tasks (reviewer side) ───────────────────────────────────────────────────
function AdminTasksTab({ currentUser, journey, tasks }) {
  const [adding, setAdding] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [form, setForm] = useState({ day: '1', title: '', description: '' });
  const sorted = useMemo(() => [...tasks].sort((a, b) => (a.day || 0) - (b.day || 0)), [tasks]);

  const baseFields = {
    onboardingId: journey.id,
    engineerEmail: journey.engineerEmail,
    mentorEmail: journey.mentorEmail,
  };

  const addTask = async () => {
    if (!form.title.trim()) return;
    try {
      await put('onboarding_tasks', {
        id: generateId(),
        ...baseFields,
        day: Number(form.day) || 1,
        title: form.title.trim(),
        description: form.description.trim(),
        status: 'todo',
        comments: [],
        createdAt: new Date().toISOString(),
        createdBy: currentUser.email,
      });
      setForm({ day: String(Math.min((Number(form.day) || 1) + 1, ONBOARDING_DAYS)), title: '', description: '' });
      toast.success('Task added.');
    } catch (e) {
      toast.error(e.message || 'Could not add the task.');
    }
  };

  const seedPlan = async () => {
    if (seeding) return;
    const ok = await confirm(
      `Add the standard ${ONBOARDING_DAYS}-day plan (${DEFAULT_PLAN.length} tasks) for ${journey.engineerName}? You can edit or delete any of them afterwards.`,
      'Use default plan'
    );
    if (!ok) return;
    setSeeding(true);
    try {
      // Sequential so a partial failure still leaves a coherent plan.
      for (const item of DEFAULT_PLAN) {
        await put('onboarding_tasks', {
          id: generateId(),
          ...baseFields,
          day: item.day,
          title: item.title,
          description: item.description,
          status: 'todo',
          comments: [],
          createdAt: new Date().toISOString(),
          createdBy: currentUser.email,
        });
      }
      toast.success(`${DEFAULT_PLAN.length}-day plan added.`);
    } catch (e) {
      toast.error(e.message || 'Could not add the plan.');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 justify-end">
        {sorted.length === 0 && (
          <button
            onClick={seedPlan}
            disabled={seeding}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
          >
            {seeding ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Use default {ONBOARDING_DAYS}-day plan
          </button>
        )}
        <button
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition"
        >
          <Plus size={15} /> {adding ? 'Close' : 'Add task'}
        </button>
      </div>

      {adding && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Day</label>
              <select
                value={form.day}
                onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {Array.from({ length: ONBOARDING_DAYS }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={String(d)}>Day {d}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1 sm:col-span-3">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Task title *</label>
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Ship your first bug fix"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">What good looks like</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
              placeholder="Give enough context that they can start without asking."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none transition resize-none"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={addTask}
              disabled={!form.title.trim()}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition disabled:opacity-50"
            >
              Add task
            </button>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <ClipboardList className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No tasks yet</h3>
          <p className="text-sm text-slate-500 mt-1">Seed the standard plan above, or add tasks one at a time.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((task) => <ReviewTaskCard key={task.id} task={task} currentUser={currentUser} />)}
        </div>
      )}
    </div>
  );
}

function ReviewTaskCard({ task, currentUser }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const meta = TASK_STATUS[task.status] || TASK_STATUS.todo;
  const role = currentUser.role === 'admin' ? 'admin' : 'mentor';

  const decide = async (status) => {
    if (busy) return;
    if (status === 'changes-requested' && !comment.trim()) {
      toast.error('Say what needs changing so they know what to do.');
      return;
    }
    setBusy(true);
    try {
      await put('onboarding_tasks', {
        ...task,
        status,
        reviewComment: comment.trim() || task.reviewComment || '',
        reviewedBy: currentUser.name || currentUser.email,
        reviewedAt: new Date().toISOString(),
      });
      setComment('');
      toast.success(status === 'approved' ? 'Task approved.' : 'Changes requested.');
    } catch (e) {
      toast.error(e.message || 'Could not update the task.');
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await confirm(`Delete "${task.title}"?`, 'Delete task');
    if (!ok) return;
    try {
      await remove('onboarding_tasks', task.id);
      toast.success('Task deleted.');
    } catch (e) {
      toast.error(e.message || 'Could not delete the task.');
    }
  };

  const addComment = async (body) => {
    const entry = {
      id: generateId(), body,
      authorName: currentUser.name, authorEmail: currentUser.email, authorRole: role,
      createdAt: new Date().toISOString(),
    };
    try {
      await put('onboarding_tasks', { ...task, comments: [...(task.comments || []), entry] });
    } catch (e) {
      toast.error(e.message || 'Could not post the comment.');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded" style={{ background: 'var(--accent)' }}>Day {task.day}</span>
              <span className={`sh-badge ${meta.badge}`}>{meta.label}</span>
            </div>
            <h4 className="font-bold text-slate-900 mt-2">{task.title}</h4>
            {task.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{task.description}</p>}
          </div>
          <button onClick={del} title="Delete task" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition shrink-0">
            <Trash2 size={15} />
          </button>
        </div>

        {task.progressNote && (
          <div className="bg-slate-50 border border-slate-150 rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">Engineer's note</span>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{task.progressNote}</p>
          </div>
        )}

        {task.reviewedAt && (
          <div className="text-xs text-slate-500">
            Last reviewed by {task.reviewedBy} · {fmtDateTime(task.reviewedAt)}
          </div>
        )}

        {task.status !== 'approved' && (
          <div className="space-y-2">
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Review note (required when asking for changes)"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => decide('approved')}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition disabled:opacity-50"
              >
                <ThumbsUp size={14} /> Approve
              </button>
              <button
                onClick={() => decide('changes-requested')}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
              >
                <RotateCcw size={14} /> Request changes
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 bg-slate-50/40 p-4">
        <CommentThread comments={task.comments} canComment onAdd={addComment} placeholder="Guide them here…" />
      </div>
    </div>
  );
}

// ── Work logs (reviewer side) ───────────────────────────────────────────────
function AdminLogsTab({ currentUser, logs }) {
  const sorted = useMemo(() => [...logs].sort((a, b) => (b.day || 0) - (a.day || 0)), [logs]);
  const role = currentUser.role === 'admin' ? 'admin' : 'mentor';

  const addComment = async (log, body) => {
    const entry = {
      id: generateId(), body,
      authorName: currentUser.name, authorEmail: currentUser.email, authorRole: role,
      createdAt: new Date().toISOString(),
    };
    try {
      await put('onboarding_logs', { ...log, comments: [...(log.comments || []), entry] });
    } catch (e) {
      toast.error(e.message || 'Could not post the comment.');
    }
  };

  if (!sorted.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
        <NotebookPen className="mx-auto text-slate-300 mb-3" size={36} />
        <h3 className="text-base font-bold text-slate-800 font-display">No work logs yet</h3>
        <p className="text-sm text-slate-500 mt-1">The engineer's daily updates will appear here as they log them.</p>
      </div>
    );
  }

  const totalHours = sorted.reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        {sorted.length} log{sorted.length === 1 ? '' : 's'}{totalHours ? ` · ${totalHours} hours reported` : ''}
      </div>
      {sorted.map((log) => (
        <div key={log.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 sm:p-5 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded" style={{ background: 'var(--accent)' }}>Day {log.day}</span>
              <span className="text-xs text-slate-500 font-mono">{fmtDate(log.date)}</span>
              {log.hours && <span className="text-xs font-semibold text-slate-600">{log.hours}h</span>}
            </div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{log.summary}</p>
            {log.blockers && (
              <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap"><span className="font-semibold">Blocked: </span>{log.blockers}</span>
              </div>
            )}
          </div>
          <div className="border-t border-slate-100 bg-slate-50/40 p-4">
            <CommentThread comments={log.comments} canComment onAdd={(body) => addComment(log, body)} placeholder="Reply to this update…" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Review & decision ───────────────────────────────────────────────────────
function ReviewTab({ currentUser, journey, tasks, logs }) {
  const [scorecard, setScorecard] = useState(journey.scorecard || {});
  const [notes, setNotes] = useState(journey.reviewNotes || '');
  const [recommendation, setRecommendation] = useState(journey.recommendation || '');
  const [saving, setSaving] = useState(false);

  const progress = journeyProgress(tasks);
  const totalHours = logs.reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);
  const scored = SCORECARD_DIMENSIONS.filter((d) => scorecard[d.key]);
  const average = scored.length
    ? (scored.reduce((s, d) => s + Number(scorecard[d.key]), 0) / scored.length).toFixed(1)
    : null;

  const save = async () => {
    if (!recommendation) { toast.error('Pick a recommendation before saving.'); return; }
    if (saving) return;
    setSaving(true);
    try {
      await put('onboarding', {
        ...journey,
        scorecard,
        reviewNotes: notes.trim(),
        recommendation,
        status: RECOMMENDATIONS[recommendation].journeyStatus,
        decidedBy: currentUser.name || currentUser.email,
        decidedAt: new Date().toISOString(),
      });
      toast.success(
        recommendation === 'hire'
          ? 'Decision saved — you can now release the contract documents.'
          : 'Decision saved.'
      );
    } catch (e) {
      toast.error(e.message || 'Could not save the decision.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Evidence summary — the facts behind the decision */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider pb-2 border-b border-slate-100 mb-3">
          The {ONBOARDING_DAYS} days at a glance
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Fact label="Tasks approved" value={`${progress.approved}/${progress.total}`} sub={`${progress.percent}%`} />
          <Fact label="Still submitted" value={progress.submitted} sub="Awaiting your review" />
          <Fact label="Days logged" value={logs.length} sub={`${totalHours || 0} hours`} />
          <Fact label="Day reached" value={`${currentDay(journey.startDate)}/${ONBOARDING_DAYS}`} />
        </div>
      </div>

      {journey.decidedAt && (
        <div className={`rounded-xl p-4 flex items-start gap-3 text-sm border ${journey.recommendation === 'no-hire' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
          <CheckCircle2 className="shrink-0 mt-0.5" size={16} />
          <div>
            <span className="font-semibold block">
              Decision recorded: {RECOMMENDATIONS[journey.recommendation]?.label || journey.recommendation}
            </span>
            By {journey.decidedBy} · {fmtDateTime(journey.decidedAt)}
          </div>
        </div>
      )}

      {/* Scorecard */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 pb-2 border-b border-slate-100">
          <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Scorecard</h4>
          {average && (
            <span className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: 'var(--accent)' }}>
              <Star size={14} /> {average} / 5 average
            </span>
          )}
        </div>

        {SCORECARD_DIMENSIONS.map((dim) => (
          <div key={dim.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="sm:w-56 shrink-0">
              <div className="text-sm font-semibold text-slate-800">{dim.label}</div>
              <div className="text-xs text-slate-500">{dim.hint}</div>
            </div>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = Number(scorecard[dim.key]) === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setScorecard((s) => ({ ...s, [dim.key]: n }))}
                    className={`w-9 h-9 rounded-lg text-sm font-bold border transition ${active ? 'text-white border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                    style={active ? { background: 'var(--accent)' } : undefined}
                    aria-label={`${dim.label}: ${n} of 5`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="space-y-1 pt-2">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Overall review notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Strengths, gaps, what support they'd need if we go ahead…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
          />
        </div>

        <div className="space-y-2 pt-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Recommendation *</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {Object.entries(RECOMMENDATIONS).map(([key, r]) => {
              const active = recommendation === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRecommendation(key)}
                  className={`px-3 py-2.5 rounded-xl text-sm font-semibold border text-left transition ${active ? 'border-transparent text-white' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  style={active ? { background: key === 'no-hire' ? '#dc2626' : key === 'extend' ? '#d97706' : '#059669' } : undefined}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button
            onClick={save}
            disabled={saving || !recommendation}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Gavel size={15} />} Save decision
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Documents ───────────────────────────────────────────────────────────────
function AdminDocsTab({ currentUser, isAdmin, journey, docs }) {
  const [uploading, setUploading] = useState(false);
  const releasable = DOCS_RELEASABLE_STATUSES.includes(journey.status);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const oversized = list.filter((f) => f.size > MAX_DOC_BYTES);
    if (oversized.length) {
      toast.error(`Files must be under ${MAX_DOC_MB}MB. Rejected: ${oversized.map((f) => f.name).join(', ')}`);
    }
    const ok = list.filter((f) => f.size <= MAX_DOC_BYTES);
    if (!ok.length) return;

    setUploading(true);
    try {
      for (const file of ok) {
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        // eslint-disable-next-line no-await-in-loop
        await put('onboarding_docs', {
          id: generateId(),
          onboardingId: journey.id,
          engineerEmail: journey.engineerEmail,
          mentorEmail: journey.mentorEmail,
          name: file.name,
          type: file.type,
          size: file.size,
          dataUrl,
          published: false,
          requiresSignature: true,
          status: 'draft',
          createdAt: new Date().toISOString(),
          uploadedBy: currentUser.email,
        });
      }
      toast.success(`${ok.length} document${ok.length === 1 ? '' : 's'} uploaded — not visible to the engineer until you share.`);
    } catch (e) {
      toast.error(e.message || 'Could not upload the document.');
    } finally {
      setUploading(false);
    }
  };

  const publish = async (doc) => {
    try {
      await put('onboarding_docs', {
        ...doc,
        published: true,
        publishedAt: new Date().toISOString(),
        publishedBy: currentUser.email,
        status: 'published',
      });
      toast.success('Shared with the engineer for signing.');
    } catch (e) {
      toast.error(e.message || 'Could not share the document.');
    }
  };

  const del = async (doc) => {
    const ok = await confirm(`Delete "${doc.name}"? This also removes any signature on it.`, 'Delete document');
    if (!ok) return;
    try {
      await remove('onboarding_docs', doc.id);
      toast.success('Document deleted.');
    } catch (e) {
      toast.error(e.message || 'Could not delete the document.');
    }
  };

  return (
    <div className="space-y-4">
      {/* Gate explanation — mirrors the process: accept first, then release */}
      <div className={`rounded-xl p-4 flex items-start gap-3 text-sm border ${releasable ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
        {releasable ? <CheckCircle2 className="shrink-0 mt-0.5" size={16} /> : <Info className="shrink-0 mt-0.5" size={16} />}
        <div>
          <span className="font-semibold block">
            {releasable ? 'Documents can be released' : 'Documents are locked until the decision is positive'}
          </span>
          {releasable
            ? 'Upload the contract documents and share them — the engineer can then read and e-sign.'
            : 'Record a "Go ahead — confirm" decision on the Review tab first. You can still upload documents now and share them later.'}
        </div>
      </div>

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-2">Upload contract documents</label>
          <div className="border-2 border-dashed border-slate-200 hover:border-slate-400 bg-slate-50/50 p-4 rounded-xl text-center transition cursor-pointer relative">
            <input
              type="file"
              accept="application/pdf,image/*"
              multiple
              disabled={uploading}
              onChange={(e) => upload(e.target.files)}
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            />
            {uploading ? (
              <Loader2 className="mx-auto text-slate-400 mb-2 animate-spin" size={22} />
            ) : (
              <Upload className="mx-auto text-slate-400 mb-2" size={22} />
            )}
            <p className="text-xs font-semibold text-slate-700">{uploading ? 'Uploading…' : 'Click to upload offer letter, NDA, contract…'}</p>
            <p className="text-[10px] text-slate-400 mt-1">PDF or image, up to {MAX_DOC_MB}MB per file</p>
          </div>
        </div>
      )}

      {docs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <FileSignature className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No documents yet</h3>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin ? 'Upload the contract documents above.' : 'An admin uploads the contract documents.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map((doc) => (
            <div key={doc.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-slate-900 truncate">{doc.name}</h4>
                    {doc.signedAt
                      ? <span className="sh-badge sh-badge-success">Signed</span>
                      : doc.published
                        ? <span className="sh-badge sh-badge-warning">Awaiting signature</span>
                        : <span className="sh-badge sh-badge-outline">Not shared</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {doc.signedAt
                      ? `Signed by ${doc.signatureName} · ${fmtDateTime(doc.signedAt)}`
                      : doc.published
                        ? `Shared ${fmtDateTime(doc.publishedAt)}`
                        : `Uploaded ${fmtDateTime(doc.createdAt)} · ${Math.round((doc.size || 0) / 1024)} KB`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  {doc.dataUrl && (
                    <a
                      href={doc.dataUrl}
                      download={doc.name}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
                    >
                      <Download size={14} /> Open
                    </a>
                  )}
                  {!doc.published && (
                    <button
                      onClick={() => publish(doc)}
                      disabled={!releasable}
                      title={releasable ? 'Share with the engineer' : 'Record a positive decision first'}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Send size={14} /> Share for signing
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => del(doc)} title="Delete document" className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>

              {doc.signedAt && doc.signatureImage && (
                <div className="border-t border-slate-100 bg-emerald-50/40 p-4 flex flex-wrap items-center gap-4">
                  <img src={doc.signatureImage} alt={`Signature of ${doc.signatureName}`} className="h-14 bg-white border border-slate-200 rounded-lg" />
                  <div className="text-xs text-slate-600">
                    <div className="flex items-center gap-1.5 font-semibold text-emerald-700"><ShieldCheck size={14} /> Electronically signed</div>
                    <div className="mt-0.5">{doc.signatureName} · consent recorded</div>
                    <div className="font-mono">{fmtDateTime(doc.signedAt)}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── New engineer drawer ─────────────────────────────────────────────────────
function NewEngineerDrawer({ currentUser, attendees, onClose, onCreated }) {
  const [form, setForm] = useState({
    engineerName: '', engineerEmail: '', role: 'Software Engineer',
    startDate: today(), mentorEmail: '', seedPlan: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const mentors = useMemo(
    () => attendees.filter((a) => (a.name || '').trim() && (a.email || '').trim()),
    [attendees]
  );

  const save = async () => {
    if (!form.engineerName.trim()) { setError('The engineer\'s name is required.'); return; }
    if (!form.engineerEmail.trim()) { setError('The engineer\'s email is required.'); return; }
    if (!form.mentorEmail) { setError('Assign a mentor — someone has to guide them.'); return; }
    setSaving(true);
    setError('');
    const mentor = mentors.find((m) => m.email === form.mentorEmail);
    const id = generateId();
    try {
      await put('onboarding', {
        id,
        engineerName: form.engineerName.trim(),
        engineerEmail: form.engineerEmail.trim().toLowerCase(),
        role: form.role.trim(),
        startDate: form.startDate,
        endDate: addDays(form.startDate, ONBOARDING_DAYS - 1),
        mentorEmail: form.mentorEmail,
        mentorName: mentor?.name || form.mentorEmail,
        status: 'active',
        scorecard: {},
        createdAt: new Date().toISOString(),
        createdBy: currentUser.email,
      });

      if (form.seedPlan) {
        for (const item of DEFAULT_PLAN) {
          // eslint-disable-next-line no-await-in-loop
          await put('onboarding_tasks', {
            id: generateId(),
            onboardingId: id,
            engineerEmail: form.engineerEmail.trim().toLowerCase(),
            mentorEmail: form.mentorEmail,
            day: item.day,
            title: item.title,
            description: item.description,
            status: 'todo',
            comments: [],
            createdAt: new Date().toISOString(),
            createdBy: currentUser.email,
          });
        }
      }

      toast.success(`${form.engineerName.trim()}'s onboarding has started.`);
      onCreated(id);
    } catch (e) {
      setError(e.message || 'Could not create the onboarding journey.');
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">New Engineer Onboarding</h3>
            <p className="text-xs text-slate-500 mt-1">Assign a mentor and start the {ONBOARDING_DAYS}-day plan</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Engineer name *</label>
            <input
              value={form.engineerName}
              onChange={(e) => set('engineerName', e.target.value)}
              placeholder="e.g. Ananya Kumar"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Work email *</label>
            <input
              type="email"
              value={form.engineerEmail}
              onChange={(e) => set('engineerEmail', e.target.value)}
              placeholder="ananyak@nativeorange.ai"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
            <p className="text-[10px] text-slate-400">They see their onboarding when they sign in with this address.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</label>
              <input
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Start date</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => set('startDate', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Mentor / leader *</label>
            <select
              value={form.mentorEmail}
              onChange={(e) => set('mentorEmail', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            >
              <option value="">Select an existing team member…</option>
              {mentors.map((m) => (
                <option key={m.id} value={m.email}>{m.name} — {m.email}</option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400">
              The mentor can set tasks, review submissions, comment and record the final decision for this engineer.
            </p>
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl p-3">
            <input type="checkbox" checked={form.seedPlan} onChange={(e) => set('seedPlan', e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-semibold block">Start from the default {ONBOARDING_DAYS}-day plan</span>
              <span className="text-xs text-slate-500">Adds {DEFAULT_PLAN.length} ready-made tasks (setup → first PR → own a feature → demo → retro). Edit or delete any of them later.</span>
            </span>
          </label>
        </div>

        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />} Start onboarding
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
